import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Radio, Volume2, VolumeX } from "lucide-react";

/**
 * Live radio player for Ibiza Global Radio.
 * Uses an isolated <audio> element (not the PlayerContext) so it doesn't
 * conflict with track previews. Auto-pauses when a preview starts.
 */
const RADIO_URL = "https://listenssl.ibizaglobalradio.com:8024/stream";

export default function RadioPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const a = new Audio();
    a.preload = "none";
    a.crossOrigin = "anonymous";
    a.src = RADIO_URL;
    audioRef.current = a;

    const onPlaying = () => { setPlaying(true); setLoading(false); setError(false); };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onError = () => { setError(true); setPlaying(false); setLoading(false); };

    a.addEventListener("playing", onPlaying);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("error", onError);
    return () => {
      a.pause();
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("error", onError);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      setLoading(true);
      // Reset src to force reconnect (radio streams often need this after pause)
      a.src = RADIO_URL + "?t=" + Date.now();
      const promise = a.play();
      if (promise && promise.catch) {
        promise.catch((err) => {
          console.error("Radio play failed:", err);
          setError(true);
          setLoading(false);
        });
      }
    }
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <div className="border border-white/10 bg-black/40 backdrop-blur-xl p-6 w-full" data-testid="radio-player">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow flex items-center gap-2">
          <Radio className="w-3 h-3" />
          Live Radio
        </div>
        {playing && (
          <span className="flex items-center gap-1 text-[9px] font-mono-plex uppercase tracking-widest text-[#FF5E00]">
            <span className="w-1.5 h-1.5 bg-[#FF5E00] rounded-full animate-pulse" /> On Air
          </span>
        )}
      </div>
      <div className="font-display font-black text-2xl leading-tight">Ibiza Global</div>
      <div className="text-xs text-white/50 font-mono-plex mt-1">
        {error ? "Stream unavailable" : loading ? "Connecting..." : playing ? "Broadcasting live" : "Deep House · Ibiza"}
      </div>

      {/* Waveform — animated only while playing */}
      <div className="mt-4 flex items-end gap-[3px] h-10">
        {Array.from({ length: 32 }).map((_, i) => (
          <div
            key={`bar-${i}`}
            className={`flex-1 ${playing ? "bg-[#FF5E00]" : "bg-[#B3FF00]/60"}`}
            style={{
              height: `${20 + Math.abs(Math.sin(i * 0.7)) * 80}%`,
              opacity: playing ? 1 : 0.5,
              transition: "background-color 0.3s ease, opacity 0.3s ease",
              animationName: playing ? "wave" : "none",
              animationDuration: `${0.9 + (i % 5) * 0.15}s`,
              animationTimingFunction: "ease-in-out",
              animationIterationCount: "infinite",
              animationDelay: `${(i % 8) * 0.08}s`,
            }}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={toggle}
          disabled={loading}
          className="w-11 h-11 bg-[#FF5E00] hover:bg-[#FF7A33] disabled:opacity-60 flex items-center justify-center transition-colors"
          data-testid="radio-play-btn"
          aria-label={playing ? "Pause radio" : "Play radio"}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <button
          onClick={toggleMute}
          className="w-9 h-9 border border-white/10 hover:border-white/30 flex items-center justify-center"
          data-testid="radio-mute-btn"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
        <a
          href="https://ibizaglobalradio.com"
          target="_blank"
          rel="noreferrer"
          className="ml-auto text-[10px] font-mono-plex uppercase tracking-widest text-white/40 hover:text-[#B3FF00]"
          data-testid="radio-source-link"
        >
          ibizaglobalradio.com ↗
        </a>
      </div>
    </div>
  );
}
